import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { DollarSign, Scissors, Clock, Check } from 'lucide-react'
import api from '../services/api'

/**
 * DNA Progress Engine
 * Visuele voortgang zonder percentages - balk groeit naar rechts.
 * Sectie-check: Euro, Zaag (materiaal), Klok (operaties) geven direct feedback.
 * Premium SaaS-feeling (Salesforce/HubSpot) voor de meubelmaker.
 */

const SECTIONS = [
  { key: 'financials', icon: DollarSign, label: 'Financiën', link: '/settings/dna' },
  { key: 'materials', icon: Scissors, label: 'Materialen', link: '/materialen' },
  { key: 'operations', icon: Clock, label: 'Operaties', link: '/master-dna?tab=hardware' },
]
// Euro, Zaag (Scissors = snijden/zaagwerk), Klok - sectie-check voor meubelmaker

// Map API sections to our 3 groups: hardware + machines = operations
const SECTION_MAP = {
  financials: ['financials'],
  materials: ['materials'],
  operations: ['hardware', 'machines'],
}

export default function DNAProgressEngine({ compact = false, onSectionClick }) {
  const navigate = useNavigate()

  const { data: dnaStatus } = useQuery({
    queryKey: ['dna-status'],
    queryFn: async () => {
      const response = await api.get('/setup/dna/status')
      return response.data
    },
    refetchInterval: 60000,
    retry: false,
  })

  const incompleteSections = dnaStatus?.incomplete_sections ?? []
  const isComplete = dnaStatus?.is_complete ?? false

  // Bereken per groep: operations = hardware AND machines
  const getSectionComplete = (sectionKey) => {
    const apiKeys = SECTION_MAP[sectionKey]
    return apiKeys.every((k) => !incompleteSections.includes(k))
  }

  const completedCount = SECTIONS.filter((s) => getSectionComplete(s.key)).length
  const progress = isComplete ? 1 : completedCount / SECTIONS.length

  const handleClick = (section) => {
    if (onSectionClick) {
      onSectionClick(section)
    } else {
      navigate(section.link)
    }
  }

  if (compact) {
    return (
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-[80px] h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-emerald-500 dark:bg-emerald-600 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progress * 100}%` }}
          />
        </div>
        <div className="flex gap-1.5">
          {SECTIONS.map((section) => {
            const Icon = section.icon
            const complete = getSectionComplete(section.key)
            return (
              <button
                key={section.key}
                onClick={() => handleClick(section)}
                className={`p-1.5 rounded-lg transition-colors ${
                  complete
                    ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-400'
                    : 'bg-gray-100 text-gray-400 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700'
                }`}
                title={`${section.label}${complete ? ' • Voltooid' : ' • Nog te configureren'}`}
              >
                {complete ? (
                  <Check className="h-4 w-4" strokeWidth={2.5} />
                ) : (
                  <Icon className="h-4 w-4" />
                )}
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/50 p-5">
      <div className="flex items-center gap-4 mb-4">
        <div className="flex-1 h-2.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progress * 100}%` }}
          />
        </div>
        {isComplete && (
          <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400 shrink-0 flex items-center gap-1">
            <Check className="h-4 w-4" /> Volledig gekalibreerd
          </span>
        )}
      </div>
      <div className="flex gap-2">
        {SECTIONS.map((section) => {
          const Icon = section.icon
          const complete = getSectionComplete(section.key)
          return (
            <button
              key={section.key}
              onClick={() => handleClick(section)}
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg border transition-all ${
                complete
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-400'
                  : 'border-slate-200 bg-slate-50 text-slate-500 hover:border-slate-300 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800/50 dark:hover:bg-slate-800'
              }`}
            >
              {complete ? (
                <Check className="h-5 w-5" strokeWidth={2.5} />
              ) : (
                <Icon className="h-5 w-5" />
              )}
              <span className="text-sm font-medium">{section.label}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
